import { useCallback, useMemo, useState } from 'react';

import { describe } from '../../dates';
import { useLanguage } from '../../contexts/language';

import styles from './style.module.css';

// The clinician's monthly sheet (F1) as a table: one column per day, the
// seven mood states as rows — elevated at the top, normal in the middle,
// depressed at the bottom, as on paper — then anxiety, irritability, hours
// slept and weight. A day without an answer is an empty cell: nothing is
// averaged, filled in or carried over.
//
// The whole column of a day selects it, which opens its card under the chart
// (#146): pointing at any of its cells marks the column, and a click on any
// of them chooses the day. The keyboard reaches each day once, through its
// heading's button, named with the full date, and a focused day is marked
// like a pointed one. Days still to come are dimmed and cannot be chosen.
//
// One look for the patient's Diary and a clinician's read of it (#89, #146):
// the measures carry the blueprint's icons and the pinned column is ruled
// off from the days. Only where the chart shows differs: the patient's is
// desktop only, where a phone shows one day at a time; onPhones, as a
// clinician reads a patient's month on every screen, a phone scrolls the
// chart sideways under a narrow pinned column of swatches and icons, named
// in a Legend. The names stay in the row headers for assistive technology
// either way.
// A measure's icon, drawn from the stylesheet's mask; its name stays in the
// row header or legend item beside it.
function iconOf(icon) {
  return <span className={ `${ styles.icon } ${ icon }` } aria-hidden='true'></span>;
}

export function MonthChart({ days, selected, today, onSelect, onPhones }) {
  const { text, language } = useLanguage();
  const [ pointed, setPointed ] = useState();
  const [ focused, setFocused ] = useState();

  const number = useMemo(function() {
    return new Intl.NumberFormat(language, { maximumFractionDigits: 1 });
  }, [ language ]);

  // The day of the cell an event happened in, when it can be chosen.
  const dayOf = useCallback(function(target) {
    const cell = target.closest('[data-day]');

    if (cell == null || cell.dataset.day > today) {
      return null;
    }

    return cell.dataset.day;
  }, [ today ]);

  const onClick = useCallback(function({ target }) {
    const date = dayOf(target);

    if (date != null) {
      onSelect(date);
    }
  }, [ dayOf, onSelect ]);

  const onPointerOver = useCallback(function({ target }) {
    setPointed(dayOf(target));
  }, [ dayOf ]);

  const onPointerLeave = useCallback(function() {
    setPointed();
  }, []);

  const onFocus = useCallback(function({ target }) {
    setFocused(dayOf(target));
  }, [ dayOf ]);

  const onBlur = useCallback(function() {
    setFocused();
  }, []);

  // A day still to come is dimmed and never marked; any other can be chosen,
  // and is marked while pointed at or focused.
  const cellClass = useCallback(function(date) {
    const classNames = [ styles.cell ];

    if (date === selected) {
      classNames.push(styles.selected);
    }

    if (date > today) {
      classNames.push(styles.future);

      return classNames.join(' ');
    }

    classNames.push(styles.available);

    if (date === pointed || date === focused) {
      classNames.push(styles.marked);
    }

    return classNames.join(' ');
  }, [ selected, today, pointed, focused ]);

  const heads = useMemo(function() {
    return days.map(function({ date }) {
      return (
        <th key={ date } scope='col' className={ cellClass(date) } data-day={ date }>
          <button className={ styles.day } type='button' value={ date } disabled={ date > today } aria-label={ describe({ day: date, language, full: true }) }>{ Number(date.slice(8)) }</button>
        </th>
      );
    });
  }, [ days, today, language, cellClass ]);

  // Rows top to bottom are moods 6 to 0. A cell holds a mark in the mood's
  // colour on the day it was recorded, and its name for assistive technology.
  const moodRows = useMemo(function() {
    return [ 6, 5, 4, 3, 2, 1, 0 ].map(function(level) {
      const cells = days.map(function({ date, mood }) {
        let mark = null;

        if (mood === level) {
          mark = <span className={ styles.mark } style={ { '--swatch': `var(--mood-${ level })` } } role='img' aria-label={ text.moods[ level ].name }></span>;
        }

        return <td key={ date } className={ cellClass(date) } data-day={ date }>{ mark }</td>;
      });

      let rowClass = styles.row;

      if (level === 3) {
        rowClass = `${ styles.row } ${ styles.normal }`;
      }

      return (
        <tr key={ level } className={ rowClass }>
          <th scope='row' className={ styles.label }>
            <span className={ styles.key } style={ { '--swatch': `var(--mood-${ level })` } }></span>

            <span className={ styles.name }>{ text.moods[ level ].name }</span>
          </th>

          { cells }
        </tr>
      );
    });
  }, [ days, text, cellClass ]);

  const measures = useMemo(function() {
    return [
      { key: 'anxiety', icon: styles.anxiety, label: text.anxiety, scale: text.anxietyScale },
      { key: 'irritability', icon: styles.irritability, label: text.irritability, scale: text.irritabilityScale },
      { key: 'sleepHours', icon: styles.sleep, label: text.hoursSlept, scale: text.hoursSlept },
      { key: 'weightKg', icon: styles.weight, label: `${ text.weight } · ${ text.kg }`, scale: `${ text.weight } · ${ text.kg }` }
    ];
  }, [ text ]);

  const valueRows = useMemo(function() {
    return measures.map(function({ key, icon, label }) {
      const cells = days.map(function(day) {
        let value = '';

        if (day[ key ] != null) {
          value = number.format(day[ key ]);
        }

        return <td key={ day.date } className={ cellClass(day.date) } data-day={ day.date }>{ value }</td>;
      });

      return (
        <tr key={ key } className={ `${ styles.row } ${ styles.values }` }>
          <th scope='row' className={ styles.label }>
            { iconOf(icon) }

            <span className={ styles.name }>{ label }</span>
          </th>

          { cells }
        </tr>
      );
    });
  }, [ days, measures, number, cellClass ]);

  let chartClass = styles.chart;

  if (onPhones === true) {
    chartClass = `${ styles.chart } ${ styles.onPhones }`;
  }

  // The Legend names the swatches and icons that the narrow pinned column
  // shows without their names, so only a chart shown on phones has one.
  const legend = useMemo(function() {
    if (onPhones !== true) {
      return null;
    }

    return (
      <details className={ styles.legend }>
        <summary className={ styles.summary }>{ text.legend }</summary>

        <div className={ styles.legendColumns }>
          <ul className={ styles.legendList }>
            {
              [ 6, 5, 4, 3, 2, 1, 0 ].map(function(level) {
                return (
                  <li key={ level } className={ styles.legendItem }>
                    <span className={ styles.key } style={ { '--swatch': `var(--mood-${ level })` } }></span>

                    { text.moods[ level ].name }
                  </li>
                );
              })
            }
          </ul>

          <ul className={ styles.legendList }>
            {
              measures.map(function({ key, icon, scale }) {
                return (
                  <li key={ key } className={ styles.legendItem }>
                    { iconOf(icon) }

                    { scale }
                  </li>
                );
              })
            }

            <li className={ styles.legendItem }>
              <span className={ `${ styles.key } ${ styles.empty }` }></span>

              { text.emptyColumn }
            </li>
          </ul>
        </div>
      </details>
    );
  }, [ onPhones, text, measures ]);

  return (
    <figure className={ chartClass }>
      <div className={ styles.scroll }>
        <table className={ styles.table } onClick={ onClick } onPointerOver={ onPointerOver } onPointerLeave={ onPointerLeave } onFocus={ onFocus } onBlur={ onBlur }>
          <caption className={ styles.caption }>{ text.monthChart }</caption>

          <thead>
            <tr>
              <td className={ styles.corner }></td>

              { heads }
            </tr>
          </thead>

          <tbody>
            { moodRows }

            { valueRows }
          </tbody>
        </table>
      </div>

      <figcaption className={ styles.note }>{ text.emptyDays }</figcaption>

      { legend }
    </figure>
  );
}
